import type { ButtonHTMLAttributes, ReactNode } from 'react';

/**
 * 크기 — 목업 `.m-chip`이 `padding:8px 14px` · `font-size:13px`이라 md가 그 전사다.
 * sm은 목업에 대응 정의가 없고 **실코드 근거로만** 둔다: 툴바·폴드 레이아웃의 칩이
 * `px-2/px-2.5 + text-xs`로 몰려 있어(BottomActionBar 4곳 · InspectionDetailFold 3곳 등)
 * md 하나로는 좁은 가로 스크롤 툴바에 들어가지 않는다.
 *
 * Tailwind 정적 스캔 대상이라 완성형 리터럴 맵으로 둔다(동적 조합 금지).
 */
const SIZE_CLASS = {
  sm: 'gap-1 px-2.5 py-1 text-xs',
  md: 'gap-[5px] px-3.5 py-2 text-[13px]',
} as const;

/**
 * 선택 상태 — 목업 `.m-chip` / `.radio-pill`이 **서로 독립으로 저작됐는데도 같은 처방**을
 * 쓴다(기본: 흰 면 + line 테두리 + ink-700 / 선택: brand 그라데이션 + brand-500 테두리 +
 * brand-700 글자). 같은 목업 안 두 컴포넌트가 일치하면 저작 습관이 아니라 규칙이다.
 *
 * hover는 **선택되지 않았을 때만** 바뀐다(목업 `.m-chip:hover:not(.active)`).
 * 선택된 칩에 hover 변화를 주면 "선택됨"과 "가리킴"이 같은 방향으로 밝아져 구분이 흐려진다.
 */
const STATE_CLASS = {
  on: 'border-brand-500 bg-gradient-to-br from-brand-50 to-[#f5f7ff] text-brand-700',
  off: 'border-line bg-white text-ink-700 hover:border-brand-200 hover:bg-surface-soft',
} as const;

export type ChipSize = keyof typeof SIZE_CLASS;

export interface ChipProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * 선택 상태. 넘기면 `aria-pressed`가 자동으로 붙는다.
   *
   * **생략하면 aria-pressed도 붙지 않는다** — 값을 적용하기만 하는 제안 칩
   * (`PhotoUploadSheet`의 최근 입력 · `TBMEditForm`의 대책 제안)은 눌린 상태가 없어서,
   * `aria-pressed={false}`가 붙으면 보조기술에 "지금 꺼져 있는 토글"이라고 거짓말을 한다.
   */
  selected?: boolean;
  /** 크기. 기본 md(13px). */
  size?: ChipSize;
  /** 라벨(아이콘 동반 가능). */
  children: ReactNode;
  /** 추가 클래스. */
  className?: string;
}

/**
 * 공용 칩 — **선택 상태를 표현하는 작은 클릭 대상**의 단일 소스.
 *
 * 왜 Badge도 Button도 아닌가:
 * - `Badge`에 클릭을 얹지 않은 이유 — role·키보드·focus 링을 떠안으면 정체가 버튼으로 바뀐다.
 * - `Button`에 넣지 않은 이유 — 버튼은 `h-8`/`h-[38px]` 같은 **고정 높이 박스**가 불변식인데
 *   칩은 줄바꿈되는 흐름 안에서 내용에 맞춰 늘어난다(패딩 기반).
 * 그 사이에 자리가 없어서 필터·토글 칩이 양쪽 이관에서 모두 남았다.
 *
 * 치수·radius·포커스 링·transition은 `Button`의 껍데기 규약과 같은 값을 쓴다 — 칩은 필터 바에서
 * 버튼·입력과 한 줄에 놓이므로 여기서 갈리면 그 줄이 어긋난다. radius가 목업 `.m-chip`의 8px이
 * 아니라 `rounded-control`(10px)인 것도 같은 이유다: 목업의 칩 계열이 스스로
 * `.seg-tab` 7px · `.m-chip` 8px · `.radio-pill` 9px · `.filter-dd` 10px로 갈려 있어 한 값으로
 * 스냅해야 하고, 프로젝트는 이미 "폼 컨트롤 = 10px"을 채택했다(반지름 2단계 결정 ①).
 *
 * 접근성:
 * - `aria-pressed`는 `selected`에서 파생된다(토글 시맨틱).
 * - **택1 그룹**은 `role="radio"` + `aria-checked`를 넘기고 부모가 `role="radiogroup"`을 쥐면 된다.
 *   전용 축을 두지 않은 이유는 **props 통과만으로 표현되기 때문** — 통과로 되는 것은 축이 아니다.
 * - `title`·`aria-label`을 포함한 모든 button 속성이 그대로 통과한다(`Badge`가 받지 않아
 *   호출부가 `<span>`으로 감싸던 문제를 여기서는 만들지 않는다).
 *
 * tone 축이 없다 — 선택 색은 brand 하나다. 도메인 색(TBM 태그의 긴급/특별 등)이 필요한 자리는
 * 이관 대상이 아니라 개별 판단으로 남긴다: 색이 값마다 다르면 그건 칩이 아니라 색으로 분류를
 * 나르는 배지에 가깝고, tone을 열면 "선택 안 됐는데 빨간 칩" 같은 조합이 곧바로 생긴다.
 */
export function Chip({
  selected,
  size = 'md',
  type = 'button',
  role,
  children,
  className = '',
  ...rest
}: ChipProps) {
  // 택1 그룹처럼 role을 바꿔 쓰는 자리에서는 aria-pressed를 내보내지 않는다 —
  // `role="radio"`에 aria-pressed가 같이 붙으면 보조기술이 토글과 라디오를 동시에 읽는다.
  const pressed = role && role !== 'button' ? undefined : selected;
  return (
    <button
      type={type}
      role={role}
      aria-pressed={pressed}
      className={`inline-flex items-center justify-center rounded-control border font-semibold tracking-[-0.005em] transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50 ${SIZE_CLASS[size]} ${selected ? STATE_CLASS.on : STATE_CLASS.off} ${className}`}
      {...rest}
    >
      {children}
    </button>
  );
}
